var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import moment from "moment";
import { useEffect, useState } from "react";
import { matchPath, useLoaderData, useLocation, useNavigate, useParams } from "react-router-dom-v5-compat";
import { Routes } from "../../Shared/components/Navigation/navigationUtils";
import { LogLevel } from "../../Shared/hooks/useLogging/Logging-types";
import { useLogging } from "../../Shared/hooks/useLogging/useLogging";
import { usePriceFormat } from "../../Shared/hooks/usePriceFormat/usePriceFormat";
import { axiosPrivate } from "../../api/axios";
import useToast from "../../hooks/useToast";
import { getDefaultMealSize } from "../components/modules/MealSize/mealSizeUtils";
import { OrderFlowErrorMessages, useSession } from "../hooks/Session/useSession";
import { useHelpers } from "../hooks/useHelpers";
import { ActionTypes } from "./OrderFlowActionTypes";
import { useOrderFlow } from "./OrderFlowContext";
import { DEFAULT_DAY_COUNT, ModuleNames, ModuleTypes, } from "./OrderFlowTypes";
const defaultLoadingText = "Loading...";
/**
 * Gets the Order Flow Model
 * @returns
 */
export const useGetModel = (editSku, editOrderId, dateChangesTakeEffect, startDate, subscriptionPeriod) => {
    var _a, _b, _c, _d;
    const { sku: skuParam } = useParams();
    const { synchronizeActiveCartProducts, synchronizeCartQuestionAnswers, getDefaultCart, getDayFilterDays, getDefaultModel, } = useHelpers();
    const { datadogLog } = useLogging();
    const { applyRecipeTypeUpcharge } = useRecipes();
    const { dispatch } = useOrderFlow();
    const [loading, setLoading] = useState(defaultLoadingText);
    const { ContinueToCheckout, restartOrderFlow } = useSession();
    const toast = useToast();
    const navigate = useNavigate();
    const location = useLocation();
    const currentSku = editSku ? editSku : skuParam;
    const currentPostalCode = (_b = (_a = location === null || location === void 0 ? void 0 : location.state) === null || _a === void 0 ? void 0 : _a.postalCode) !== null && _b !== void 0 ? _b : undefined;
    const currentLocation = (_d = (_c = location === null || location === void 0 ? void 0 : location.state) === null || _c === void 0 ? void 0 : _c.location) !== null && _d !== void 0 ? _d : undefined;
    const isCheckout = matchPath("/:sku/Checkout", location.pathname);
    const loadingText = useLoaderData();
    // ========== UPDATE LOADING TEXT IF CONFIGURED ==========
    useEffect(() => {
        let mounted = true;
        if (mounted && loadingText) {
            setLoading(loadingText);
        }
        return () => {
            mounted = false;
        };
    }, [loadingText]);
    // ========== FETCH MODEL ==========
    useEffect(() => {
        if (isCheckout === null) {
            getModel();
        }
    }, []);
    const getModel = () => __awaiter(void 0, void 0, void 0, function* () {
        yield axiosPrivate
            .get(`/OrderFlow/GetModel?timestamp=${new Date().getTime()}`, {
            params: {
                zip: currentPostalCode,
                locationSku: currentLocation === null || currentLocation === void 0 ? void 0 : currentLocation.locationSku,
                sku: currentSku,
                editOrderId: editOrderId,
                dateChangesTakeEffect: dateChangesTakeEffect,
                startDate: startDate,
            },
            responseType: "json",
            headers: { Accept: "application/json" },
        })
            .then(({ data }) => {
            var _a, _b, _c;
            // SFF mobile app receives data as string for some reason; re-parse to be safe
            data = JSON.parse(JSON.stringify(data));
            if (!data.success) {
                if (data.invalidSku) {
                    datadogLog(`Got to Order Flow with an invalid flow Sku: ${currentSku} `, LogLevel.WARN);
                    navigate(Routes.NOT_FOUND, { replace: true, state: Object.assign(Object.assign({}, location.state), { prevRoute: location.pathname }) });
                }
                else {
                    restartOrderFlow(currentSku, undefined, false);
                }
                return;
            }
            //If no modules are visible we are ready for checkout?
            //TODO: Is this still necessary? What was this for?
            let isAnyModuleVisible = false;
            data.model.Flow.FlowModules.forEach((flowModule) => {
                try {
                    flowModule.ModuleConfig = JSON.parse(flowModule.ModuleConfig);
                    flowModule.Module.AvailableConfig = JSON.parse(flowModule.Module.AvailableConfig);
                    if (flowModule.ModuleConfig["Visible"] === undefined ||
                        flowModule.ModuleConfig["Visible"] === null ||
                        flowModule.ModuleConfig["Visible"] === true) {
                        isAnyModuleVisible = true;
                    }
                }
                catch (e) {
                    restartOrderFlow(currentSku);
                    datadogLog(`Caught exception while validating Flow Modules. Message: ${e.message}`, LogLevel.ERROR, undefined, e);
                }
            });
            let defaultCart;
            //If we are coming back from checkout, we should start at the page with the requirements module.
            //Otherwise, start at page 1
            const pageToStart = ((_a = location.state) === null || _a === void 0 ? void 0 : _a.page) > 0 ? (_b = location.state) === null || _b === void 0 ? void 0 : _b.page : 1;
            if (!!isCheckout && !data.model.SavedCart) {
                datadogLog("Got to Checkout with an expired cart", LogLevel.INFO);
                toast.push(OrderFlowErrorMessages.EXPIRED_CART, { variant: "info" });
                navigate(`/${currentSku}/Location`, { replace: true });
            }
            if (data.model.SavedCart && !data.model.IsEditFlow) {
                //Saved cart is validated in OrderFlowController
                defaultCart = JSON.parse(data.model.SavedCart.CartJson);
                defaultCart.currentPage = pageToStart;
                synchronizeActiveCartProducts(defaultCart.products, data.model.Products);
                synchronizeCartQuestionAnswers(defaultCart.questionAnswers, data.model.Flow.FlowModules.filter((module) => module.ModuleName === ModuleNames.QUESTIONS));
            }
            else {
                //Postal code or location is required for Order Flow. Go back to Location page to select one.
                if (!(currentPostalCode || data.model.PostalCode) && !currentLocation && !data.model.IsEditFlow) {
                    datadogLog("Got to Order Flow without a location or zip", LogLevel.WARN);
                    navigate(`/${currentSku}/Location`, { replace: true });
                    return;
                }
                if (currentPostalCode) {
                    data.model.PostalCode = currentPostalCode;
                }
                if (currentLocation) {
                    data.model.Location = currentLocation;
                }
                defaultCart = getDefaultCart(data.model, pageToStart, subscriptionPeriod);
            }
            const defaultModel = getDefaultModel(Object.assign(Object.assign({}, data.model), { Recipes: [], RecipeFetchStatus: {
                    error: false,
                    completed: false,
                } }));
            if (editOrderId) {
                defaultCart.editOrderId = editOrderId;
            }
            if (!isAnyModuleVisible) {
                setLoading("Redirecting...");
                ContinueToCheckout(defaultCart, defaultModel)
                    .then((res) => {
                    if (res && res.success && res.continueToCheckout) {
                        const orderFlowState = {
                            cart: defaultCart,
                            model: defaultModel,
                            // TODO REPLACE AND REMOVE ALL INSTANCES OF AND REFERENCES TO pricingBreakdown WITH EQUIVALENT FROM BALANCE OBJECT
                            pricingBreakdown: undefined,
                            sku: defaultModel.Sku,
                        };
                        navigate({
                            pathname: `/${defaultModel.Sku}/Checkout`,
                        }, { state: Object.assign(Object.assign({}, location.state), { orderFlowState }) });
                    }
                    else {
                        datadogLog("Failed to navigate to checkout in getModel", LogLevel.ERROR);
                        restartOrderFlow(currentSku);
                    }
                })
                    .catch((err) => {
                    datadogLog(`Caught exception trying ContinueToCheckout. Message: ${err.message}`, LogLevel.ERROR, undefined, err);
                    restartOrderFlow(currentSku);
                });
            }
            dispatch({ type: ActionTypes.SET_MODEL, payload: defaultModel });
            const mealSizeModule = data.model.Flow.FlowModules.find((fm) => fm.Module.ModuleType === ModuleTypes.MEAL_SIZE);
            if (mealSizeModule && defaultModel.IsEditFlow) {
                const mealSizeModuleConfig = mealSizeModule === null || mealSizeModule === void 0 ? void 0 : mealSizeModule.ModuleConfig;
                const defaultMealSize = getDefaultMealSize(mealSizeModuleConfig === null || mealSizeModuleConfig === void 0 ? void 0 : mealSizeModuleConfig.DefaultSelectionMode, defaultModel.SubscriptionMeals, defaultModel.Program.ProgramMeals, defaultModel.MealSizes);
                defaultCart.mealSize = defaultMealSize;
            }
            /**
             * We ignore the cartMeals and template returns(which is accomplished by passing [] as their value)
             * because those values are saved in the CartJson and already have the upcharge applied.
             * However, for the 'Program' value, it is not saved in CartJson.
             * Therefore, we retrieve the value from the GetModel query results,
             * which contains the ProgramMeal.Price without the recipeTypeUpcharge.
             * We update it here.
             */
            const { updatedProgramMeals } = applyRecipeTypeUpcharge(defaultModel, undefined, defaultCart.recipeType, [], []);
            dispatch({ type: ActionTypes.SET_PROGRAM_MEALS, payload: updatedProgramMeals });
            if (defaultModel.dayFilterConfig) {
                const dayFilterDays = getDayFilterDays(defaultCart.daysOfWeek, undefined, defaultModel.DeliveryDays, defaultCart.startDate, defaultCart.isSubscription, defaultCart.subscriptionPeriod, defaultCart.oneTimeOrderPeriod, defaultModel.dayFilterConfig.OnlySelectedDaysOfWeek, defaultModel.dayFilterConfig.OnlyDaysWithDefaultMeals, defaultModel.GeneralSettings.OnlyShowDeliveryDays, (_c = defaultModel.dayFilterConfig.DayCount) !== null && _c !== void 0 ? _c : DEFAULT_DAY_COUNT);
                let selectedDay = defaultCart.selectedDay;
                if (!selectedDay || !dayFilterDays.find((d) => moment(d).isSame(selectedDay, "date"))) {
                    selectedDay = dayFilterDays.length > 0 ? dayFilterDays[0] : undefined;
                }
                dispatch({ type: ActionTypes.SET_SELECTED_DAY, payload: moment(selectedDay) });
                dispatch({ type: ActionTypes.SET_DAY_FILTER_DAYS, payload: dayFilterDays });
            }
            const originalSelections = {
                daysOfWeek: defaultCart.daysOfWeek,
                mealSize: defaultCart.mealSize,
                mealQuantity: defaultCart.meals.map((meal) => {
                    return {
                        programMealId: meal.ProgramMealId,
                        mealDate: meal.MealDate,
                        quantity: meal.Quantity,
                    };
                }),
                mealBundles: defaultCart.bundles.flatMap((bundle) => {
                    return {
                        name: bundle.BundleName,
                        quantity: bundle.Quantity,
                        meals: bundle.BundleMeals.map((bundleMeal) => {
                            return {
                                programMealId: bundleMeal.ProgramMealId,
                                quantity: bundleMeal.Quantity,
                                dayOfWeek: bundleMeal.DayOfWeek,
                            };
                        }),
                    };
                }),
                products: defaultCart.products
                    .filter((p) => p.quantity > 0)
                    .map((product) => {
                    return {
                        id: product.productId,
                        quantity: product.quantity,
                        dayOfWeek: product.DayOfWeek,
                    };
                }),
            };
            defaultCart.originalSelections = originalSelections;
            dispatch({ type: ActionTypes.SET_CART, payload: defaultCart });
        })
            .catch((err) => {
            restartOrderFlow(currentSku);
            datadogLog(`Caught exception while loading OrderFlow model. Message: ${err.message}`, LogLevel.ERROR, undefined, err);
        })
            .then(() => setLoading(undefined));
    });
    return { loading, setLoading, getModel };
};
export const useRecipes = () => {
    const [loading, setLoading] = useState(false);
    const { store } = useOrderFlow();
    const { formatPrice } = usePriceFormat();
    const getSelectedRecipes = (payload) => __awaiter(void 0, void 0, void 0, function* () {
        setLoading(true);
        const response = yield axiosPrivate.post(`/OrderFlow/GetSelectedRecipes`, payload);
        const data = response.data;
        setLoading(false);
        return data;
    });
    const applyRecipeTypeUpcharge = (model, oldRecipeType, newRecipeType, cartMeals, template) => {
        var _a, _b;
        const oldRecipeTypeUpcharge = oldRecipeType && oldRecipeType.recipeTypePrice >= 0 ? oldRecipeType.recipeTypePrice : 0;
        const newRecipeTypeUpcharge = newRecipeType && newRecipeType.recipeTypePrice >= 0 ? newRecipeType.recipeTypePrice : 0;
        const newUpcharge = newRecipeTypeUpcharge - oldRecipeTypeUpcharge;
        const updatedProgramMeals = (_b = (_a = model === null || model === void 0 ? void 0 : model.Program) === null || _a === void 0 ? void 0 : _a.ProgramMeals) === null || _b === void 0 ? void 0 : _b.map((programMeal) => {
            const newPrice = programMeal.Price + newUpcharge;
            const formattedPrice = model ? formatPrice({ displayPriceOverride: newPrice }) : newPrice;
            return Object.assign(Object.assign({}, programMeal), { Price: newPrice, PriceDisplay: formattedPrice });
        });
        const updatedCartMeals = cartMeals === null || cartMeals === void 0 ? void 0 : cartMeals.map((meal) => {
            var _a;
            const newAssignments = (_a = meal.RecipeAssignments) === null || _a === void 0 ? void 0 : _a.map((recipe) => {
                if (!(recipe === null || recipe === void 0 ? void 0 : recipe.RecipeAssignmentPrice))
                    return recipe;
                const newRecipePrice = recipe.RecipeAssignmentPrice + newUpcharge;
                return Object.assign(Object.assign({}, recipe), { RecipePrice: newRecipePrice, RecipeAssignmentPrice: newRecipePrice });
            });
            return Object.assign(Object.assign({}, meal), { RecipeAssignments: newAssignments, Price: meal.Price + newUpcharge });
        });
        const updatedTemplate = template === null || template === void 0 ? void 0 : template.map((templateMeal) => {
            const newPrice = templateMeal.Price + newUpcharge;
            return Object.assign(Object.assign({}, templateMeal), { Price: newPrice });
        });
        return { updatedProgramMeals, updatedTemplate, updatedCartMeals };
    };
    const recipeModuleDisplayRequirementCheck = (requirement) => {
        let requirementResponse = undefined;
        const requirements = store.model.requirements;
        const req = requirements.find((r) => r.page == store.cart.currentPage);
        const requirementsModuleConfig = req ? req.moduleConfig : undefined;
        const EnforceRecipeTypeSelection = requirementsModuleConfig
            ? requirementsModuleConfig.EnforceRecipeTypeSelection
            : undefined;
        if (requirement && EnforceRecipeTypeSelection && !store.cart.recipeType) {
            requirementResponse = generateResponse(false, requirement);
        }
        return requirementResponse;
    };
    const generateResponse = (meetsRequirement, requirement) => {
        let errorMsg = "N/A";
        if (requirement) {
            if (requirement.ErrorMsg) {
                errorMsg = requirement.ErrorMsg;
            }
            else if (requirement.TextValue) {
                errorMsg = requirement.TextValue;
            }
        }
        return {
            meetsRequirements: meetsRequirement,
            errorMessage: errorMsg,
        };
    };
    return { getSelectedRecipes, applyRecipeTypeUpcharge, recipeModuleDisplayRequirementCheck, loading };
};
/**
 * Loader function to fetch the `LoadingText` property from the Order Flow configurable text settings.
 * This text is used to display a loading message while the order model is being retrieved.
 *
 * If the request is successful, the function returns the `LoadingText` property from the server response.
 * If an error occurs or the `LoadingText` is missing, a default loading message is returned instead.
 *
 * @param {Params<string>} params - The parameters passed to the loader, typically containing the SKU.
 * @returns {Promise<string>} - A promise that resolves to the `LoadingText` string or a default value if an error occurs.
 */
export const OrderFlowLoader = (params) => {
    return axiosPrivate
        .get(`/Account/OrderFlowLevelConfigurableTextSettings?sku=${params.sku}`)
        .then((res) => res.data.ConfigurableTextSettings.LoadingText || defaultLoadingText)
        .catch((e) => {
        console.error(e);
        return defaultLoadingText;
    });
};
